import { useFrame, useThree } from '@react-three/fiber';
import { useRef, useEffect } from 'react';
import * as THREE from 'three';

const SceneController = ({ transitionState, planetRefs, keyframes, smoothScrollY, lastFocusIndex }) => {
  const { camera } = useThree();
  const originalCameraState = useRef(null);
  const totalScrollableHeight = useRef(document.body.scrollHeight - window.innerHeight);

  useEffect(() => {
    if (!originalCameraState.current) {
        originalCameraState.current = {
            position: camera.position.clone(),
            fov: camera.fov,
        };
    }
  }, [camera]);

  useFrame(() => {
    if (transitionState.active && originalCameraState.current) {
      // === 1. HYPERSPACE TRANSITION LOGIC ===
      const elapsedTime = performance.now() - transitionState.startTime;
      let progress = Math.min(elapsedTime / transitionState.duration, 1);
      const easedProgress = progress * progress * progress; // Ease-in for acceleration

      const targetPlanet = planetRefs.current[transitionState.targetPlanetIndex];
      if (targetPlanet && targetPlanet.group) {
        const targetPosition = new THREE.Vector3();
        targetPlanet.group.getWorldPosition(targetPosition);

        // Animate camera position and Field of View
        camera.position.lerp(targetPosition, easedProgress * 0.15);
        camera.fov = THREE.MathUtils.lerp(originalCameraState.current.fov, 140, easedProgress);
        camera.updateProjectionMatrix();

        // Animate all 3D planets
        planetRefs.current.forEach((planetApi, i) => {
          if (!planetApi || !planetApi.group) return;
          if (i === transitionState.targetPlanetIndex) {
            const targetScale = 1 + easedProgress * 80; // Target grows immensely
            planetApi.group.scale.set(targetScale, targetScale, targetScale);
          } else {
            const targetScale = Math.max(0, 1 - easedProgress); // Others shrink to nothing
            planetApi.group.scale.set(targetScale, targetScale, targetScale);
          }
        });
      }
    } else if (originalCameraState.current) {
      // === 2. REGULAR SCROLLING LOGIC FOR 3D OBJECTS ===

      // Gently reset camera if it was in a transition
      if (camera.fov !== originalCameraState.current.fov) {
        camera.position.lerp(originalCameraState.current.position, 0.1);
        camera.fov = THREE.MathUtils.lerp(camera.fov, originalCameraState.current.fov, 0.1);
        camera.updateProjectionMatrix();
      }

      const scrollPercent = totalScrollableHeight.current > 0
        ? (smoothScrollY.current / totalScrollableHeight.current) * 100
        : 0;

      const numPlanets = planetRefs.current.length;
      if (numPlanets > 0) {
        const totalSwitches = numPlanets - 1;
        const scrollAsProgress = (scrollPercent / 100) * totalSwitches;
        
        // This is the logic moved from your original HomePage.js
        const fromFocusIndex = Math.floor(scrollAsProgress);
        const toFocusIndex = fromFocusIndex + 1;
        const lerpFactor = scrollAsProgress % 1;
        lastFocusIndex.current = Math.round(scrollAsProgress);

        planetRefs.current.forEach((planetApi, i) => {
            if (!planetApi) return;
            const fromKeyframeIndex = (i - fromFocusIndex + numPlanets) % numPlanets;
            const toKeyframeIndex = (i - toFocusIndex + numPlanets) % numPlanets;

            const pos1 = keyframes.positions[fromKeyframeIndex];
            const pos2 = keyframes.positions[toKeyframeIndex];
            const scale1 = keyframes.scales[fromKeyframeIndex];
            const scale2 = keyframes.scales[toKeyframeIndex];
            const opacity1 = keyframes.opacities[fromKeyframeIndex];
            const opacity2 = keyframes.opacities[toKeyframeIndex];
            
            const currentPosition = new THREE.Vector3().lerpVectors(pos1, pos2, lerpFactor);
            const baseScale = new THREE.Vector3().lerpVectors(scale1, scale2, lerpFactor);
            const currentOpacity = THREE.MathUtils.lerp(opacity1, opacity2, lerpFactor);
            
            const minVisibility = 0.35;
            const visibilityMultiplier = THREE.MathUtils.lerp(minVisibility, 1.0, currentOpacity);
            const finalScale = baseScale.clone().multiplyScalar(visibilityMultiplier);
            const focusAmount = (i === lastFocusIndex.current) ? 1.0 : 0.0;
            planetApi.update(currentPosition, finalScale, currentOpacity, focusAmount);
        });
      }
    }
  });

  return null;
};

export default SceneController;